import { defineStore } from 'pinia'
import { ref } from 'vue'
import * as golfApi from '@/api/golf'
import { extractErrorMessage } from '@/helpers/error-parsers'
import {
  GolfFinancialReportData,
  GolfFinancialReportSummary,
  GolfFinancialReportParams,
} from '@/api/golf'

export const useGolfFinancialStore = defineStore('golf-financial', () => {
  const reportData = ref<GolfFinancialReportData | null>(null)
  const summary = ref<GolfFinancialReportSummary | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchFinancialReport = async (params?: GolfFinancialReportParams) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfFinancialReport(params)
      const responseData = response.data.data as any
      const data =
        responseData && responseData.data ? responseData.data : responseData
      reportData.value = data || null
      summary.value = data?.summary || null
      return data
    } catch (e) {
      error.value = extractErrorMessage(e, 'Failed to fetch financial report')
      console.error('Error fetching financial report:', e)
      reportData.value = null
      summary.value = null
      return null
    } finally {
      isLoading.value = false
    }
  }

  const exportFinancialReport = async (params?: GolfFinancialReportParams) => {
    try {
      const response = await golfApi.getGolfFinancialReport({
        ...params,
        format: 'xlsx',
      })
      const blob = new Blob([response.data as any], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const dateRangeStr =
        params?.date_from || params?.date_to
          ? `${params.date_from || 'start'}_${params.date_to || 'end'}`
          : 'all_time'
      link.download = `golf-financial-report-${dateRangeStr}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      return true
    } catch (e) {
      console.error('Error exporting financial report:', e)
      throw e
    }
  }

  const resetError = () => {
    error.value = null
  }

  return {
    reportData,
    summary,
    isLoading,
    error,
    fetchFinancialReport,
    exportFinancialReport,
    resetError,
  }
})
